import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, AlertCircle, TrendingUp, Brain, RefreshCw, Sparkles, CheckCircle, Clock, ArrowUpRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailSummaryDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterCounts, setFilterCounts] = useState({
    total: 0,
    unread: 0,
    done: 0,
    exception: 0,
    followup: 0
  });

  useEffect(() => {
    fetchSummary();
    fetchFilterCounts();
  }, []);

  const fetchFilterCounts = async () => {
    try {
      // Fetch inbox emails to calculate counts
      const response = await axios.get(`${API}/mailbox/inbox`);
      const emails = response.data.emails || [];
      
      // Fetch flags for all emails
      const flagPromises = emails.map(email => 
        axios.get(`${API}/mailbox/flags/${email.id}`)
          .then(res => res.data.flag)
          .catch(() => '')
      );
      const flags = await Promise.all(flagPromises);
      
      // Calculate counts
      setFilterCounts({
        total: emails.length,
        unread: emails.filter(e => !e.is_read).length,
        done: flags.filter(f => f === 'Done').length,
        exception: flags.filter(f => f === 'Exception').length,
        followup: flags.filter(f => f === 'Follow-up').length
      });
    } catch (error) {
      console.error('Error fetching filter counts:', error);
    }
  };

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/mailbox/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching email summary:', error);
      toast.error('Failed to load email summary');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0072FF] mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Analyzing your emails with AI...</p>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate-600 dark:text-slate-400">Failed to load email summary</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-[#0072FF] to-[#00C6FB] rounded-xl flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">AI Email Summary</h1>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Last updated: {new Date(summary.last_updated).toLocaleTimeString()}
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              fetchSummary();
              fetchFilterCounts();
            }}
            className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors flex items-center space-x-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>

        {/* Filter Chips */}
        <div className="flex flex-wrap gap-3">
          <div className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg flex items-center space-x-2 shadow-md">
            <Mail className="w-4 h-4" />
            <span className="font-semibold">{filterCounts.total}</span>
            <span className="text-sm opacity-90">Total Emails</span>
          </div>
          
          <div className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg flex items-center space-x-2 shadow-md">
            <AlertCircle className="w-4 h-4" />
            <span className="font-semibold">{filterCounts.unread}</span>
            <span className="text-sm opacity-90">Unread</span>
          </div>
          
          <div className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg flex items-center space-x-2 shadow-md">
            <CheckCircle className="w-4 h-4" />
            <span className="font-semibold">{filterCounts.done}</span>
            <span className="text-sm opacity-90">Done</span>
          </div>
          
          <div className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg flex items-center space-x-2 shadow-md">
            <AlertCircle className="w-4 h-4" />
            <span className="font-semibold">{filterCounts.exception}</span>
            <span className="text-sm opacity-90">Exception</span>
          </div>
          
          <div className="px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg flex items-center space-x-2 shadow-md">
            <ArrowUpRight className="w-4 h-4" />
            <span className="font-semibold">{filterCounts.followup}</span>
            <span className="text-sm opacity-90">Follow-up</span>
          </div>
        </div>
      </div>

      {/* AI Insights */}
      {summary.ai_insights && summary.ai_insights.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white dark:bg-slate-900 rounded-xl shadow-sm p-6"
        >
          <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center space-x-2">
            <Brain className="w-5 h-5 text-[#0072FF]" />
            <span>AI Insights</span>
          </h2>
          <div className="space-y-3">
            {summary.ai_insights.map((insight, index) => (
              <div
                key={index}
                className={`flex items-start space-x-3 p-4 rounded-lg ${
                  insight.type === 'warning'
                    ? 'bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800'
                    : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                }`}
              >
                <AlertCircle className={`w-5 h-5 mt-0.5 ${
                  insight.type === 'warning' ? 'text-orange-600' : 'text-red-600'
                }`} />
                <p className={`text-sm ${
                  insight.type === 'warning'
                    ? 'text-orange-900 dark:text-orange-200'
                    : 'text-red-900 dark:text-red-200'
                }`}>
                  {insight.message}
                </p>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* AI-Generated Summary */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-gradient-to-br from-[#0A34A0] via-[#0072FF] to-[#00C6FB] rounded-xl shadow-lg p-6 text-white"
      >
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
            <Brain className="w-6 h-6" />
          </div>
          <h2 className="text-xl font-bold">AI Analysis</h2>
        </div>
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
          <p className="text-white/90 leading-relaxed whitespace-pre-wrap">
            {summary.summary}
          </p>
        </div>
      </motion.div>

      {/* Important Emails List */}
      {summary.important_emails && summary.important_emails.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white dark:bg-slate-900 rounded-xl shadow-sm p-6"
        >
          <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span>Priority Emails</span>
          </h2>
          <div className="space-y-3">
            {summary.important_emails.map((email, index) => (
              <div
                key={index}
                className="flex items-start space-x-3 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <CheckCircle className="w-5 h-5 text-slate-400 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                    {email.subject}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    From: {email.from}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default EmailSummaryDashboard;
